import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useActivity } from '../App';
import toast from 'react-hot-toast';

const ALL_PERMISSIONS = [
  { key: 'dashboard',       label: 'Dashboard',           group: 'General'  },
  { key: 'users.view',      label: 'View Users',          group: 'Users'    },
  { key: 'users.edit',      label: 'Edit Users',          group: 'Users'    },
  { key: 'users.delete',    label: 'Delete Users',        group: 'Users'    },
  { key: 'roles.view',      label: 'View Roles',          group: 'Roles'    },
  { key: 'roles.create',    label: 'Create Roles',        group: 'Roles'    },
  { key: 'roles.edit',      label: 'Edit Roles',          group: 'Roles'    },
  { key: 'roles.delete',    label: 'Delete Roles',        group: 'Roles'    },
  { key: 'admins',          label: 'Admin Management',    group: 'Admins'   },
  { key: 'reports',         label: 'View Reports',        group: 'Reports'  },
  { key: 'reports.export',  label: 'Export Reports',      group: 'Reports'  },
  { key: 'content',         label: 'Manage Content',      group: 'CMS'      },
  { key: 'content.publish', label: 'Publish Content',     group: 'CMS'      },
  { key: 'notifications',   label: 'Notifications',       group: 'Notifs'   },
  { key: 'activity_logs',   label: 'Activity Logs',       group: 'System'   },
  { key: 'settings',        label: 'App Settings',        group: 'System'   },
];

const PERM_GROUPS = ALL_PERMISSIONS.reduce((acc, p) => {
  if (!acc[p.group]) acc[p.group] = [];
  acc[p.group].push(p);
  return acc;
}, {});

const PALETTE = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4', '#8b5cf6', '#ec4899', '#14b8a6'];

// ── Default WalkBattle roles (source of truth for permissions doc) ─────────────
const DEFAULT_ROLES = [
  { id: 1, name: 'SUPER_ADMIN', description: 'Full system access – all permissions', permissions: ['*'],              color: '#ef4444' },
  { id: 2, name: 'MANAGER',     description: 'User management, reports & notifications', permissions: ['dashboard','users.view','users.edit','reports','reports.export','notifications','activity_logs','admins','roles.view'], color: '#6366f1' },
  { id: 3, name: 'MARKETING',   description: 'Content, campaigns & analytics',      permissions: ['dashboard','reports','content','content.publish','notifications'], color: '#f59e0b' },
  { id: 4, name: 'SUPPORT',     description: 'View-only access to user data',        permissions: ['dashboard','users.view','activity_logs'],                          color: '#22c55e' },
];

const RoleModal = ({ role, onClose, onSave }) => {
  const isEdit = !!role;
  const [form, setForm] = useState(role || {
    name: '', description: '', color: PALETTE[0], permissions: [],
  });

  const togglePerm = (key) => setForm(p => ({
    ...p,
    permissions: p.permissions.includes(key)
      ? p.permissions.filter(x => x !== key)
      : [...p.permissions, key],
  }));

  const toggleGroup = (group) => {
    const keys = PERM_GROUPS[group].map(p => p.key);
    const all  = keys.every(k => form.permissions.includes(k));
    setForm(p => ({
      ...p,
      permissions: all
        ? p.permissions.filter(k => !keys.includes(k))
        : [...new Set([...p.permissions, ...keys])],
    }));
  };

  return (
    <div style={overlayS}>
      <div style={{ ...modalS, maxWidth: '620px' }}>
        <div style={modalHeaderS}>
          <h3 style={{ fontWeight: 700 }}>{isEdit ? '✏️ Edit Role' : '➕ Create Role'}</h3>
          <button style={closeBtnS} onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={labelS}>Role Name *</label>
              <input style={inputS} value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. MARKETING" />
            </div>
            <div>
              <label style={labelS}>Badge Color</label>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px' }}>
                {PALETTE.map(c => (
                  <div key={c} onClick={() => setForm(p => ({ ...p, color: c }))}
                    style={{ width: '24px', height: '24px', borderRadius: '50%', background: c, cursor: 'pointer', border: form.color === c ? '3px solid white' : '3px solid transparent', outline: form.color === c ? `2px solid ${c}` : 'none' }} />
                ))}
              </div>
            </div>
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={labelS}>Description</label>
            <input style={inputS} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="What can this role do?" />
          </div>

          <label style={labelS}>Permissions</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px', maxHeight: '300px', overflowY: 'auto', padding: '4px' }}>
            {Object.entries(PERM_GROUPS).map(([group, perms]) => {
              const allChecked = perms.every(p => form.permissions.includes(p.key));
              return (
                <div key={group} style={{ background: 'var(--bg-tertiary)', borderRadius: '10px', padding: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>{group}</span>
                    <label style={{ cursor: 'pointer', fontSize: '0.72rem', color: 'var(--accent-primary)' }}>
                      <input type="checkbox" checked={allChecked} onChange={() => toggleGroup(group)} style={{ marginRight: '4px' }} />
                      All
                    </label>
                  </div>
                  {perms.map(p => (
                    <label key={p.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', cursor: 'pointer', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                      <input type="checkbox" checked={form.permissions.includes(p.key)} onChange={() => togglePerm(p.key)} />
                      {p.label}
                    </label>
                  ))}
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
            <button style={cancelBtnS} onClick={onClose}>Cancel</button>
            <button style={primaryBtnS} onClick={() => {
              if (!form.name.trim()) { toast.error('Role name is required'); return; }
              onSave(form);
            }}>
              {isEdit ? 'Save Changes' : 'Create Role'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main ──────────────────────────────────────────────────────────────────────
const RoleManagement = () => {
  const { isSuperAdmin } = useAuth();
  const { addLog } = useActivity();

  const [roles,  setRoles]  = useState(DEFAULT_ROLES);
  const [modal,  setModal]  = useState(null);  // null | 'add' | role object

  const handleSave = (form) => {
    if (modal === 'add') {
      const newRole = { ...form, id: Date.now() };
      setRoles(p => [...p, newRole]);
      addLog('Role Created', form.name, 'create');
      toast.success('Role created!');
    } else {
      setRoles(p => p.map(r => r.id === modal.id ? { ...r, ...form } : r));
      addLog('Role Updated', form.name, 'update');
      toast.success('Role updated!');
    }
    setModal(null);
  };

  const handleDelete = (role) => {
    if ([1, 2, 3, 4].includes(role.id)) { toast.error("Cannot delete built-in roles"); return; }
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    setRoles(p => p.filter(r => r.id !== role.id));
    addLog('Role Deleted', role.name, 'delete');
    toast.success('Role deleted');
  };

  return (
    <div className="animate-in">
      <div className="page-header">
        <div className="page-header-left">
          <div className="breadcrumb"><span>Management</span> / <span className="current">Roles</span></div>
          <h1>Role Management</h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            Define roles and their permission scopes for the admin panel
          </p>
        </div>
        {isSuperAdmin && (
          <button style={primaryBtnS} onClick={() => setModal('add')}>➕ Create Role</button>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '16px' }}>
        {roles.map(role => {
          const isBuiltIn = [1, 2, 3, 4].includes(role.id);
          return (
            <div key={role.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderTop: `4px solid ${role.color}`, borderRadius: '12px', padding: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontWeight: 800, fontSize: '0.95rem', color: role.color }}>{role.name}</span>
                    {isBuiltIn && <span style={{ fontSize: '0.65rem', background: 'var(--bg-tertiary)', color: 'var(--text-muted)', padding: '2px 6px', borderRadius: '4px' }}>Built-in</span>}
                  </div>
                  <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '4px' }}>{role.description}</p>
                </div>
                {isSuperAdmin && (
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button style={{ ...actionBtnS, color: 'var(--accent-primary)' }} onClick={() => setModal(role)}>✏️</button>
                    {!isBuiltIn && <button style={{ ...actionBtnS, color: 'var(--accent-danger)' }} onClick={() => handleDelete(role)}>🗑️</button>}
                  </div>
                )}
              </div>

              {/* Permissions */}
              <div>
                {role.permissions.includes('*') ? (
                  <span style={{ fontSize: '0.78rem', background: '#ef444420', color: '#ef4444', padding: '3px 10px', borderRadius: '20px', fontWeight: 600 }}>
                    ⚡ All Permissions
                  </span>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {role.permissions.map(p => {
                      const meta = ALL_PERMISSIONS.find(x => x.key === p);
                      return (
                        <span key={p} style={{ fontSize: '0.72rem', background: role.color + '15', color: role.color, padding: '2px 8px', borderRadius: '20px', fontWeight: 500 }}>
                          {meta?.label || p}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={{ marginTop: '12px', fontSize: '0.73rem', color: 'var(--text-muted)' }}>
                {role.permissions.includes('*') ? 'Unlimited' : `${role.permissions.length} permission${role.permissions.length !== 1 ? 's' : ''}`}
              </div>
            </div>
          );
        })}
      </div>

      {!isSuperAdmin && (
        <div style={{ marginTop: '24px', padding: '14px 18px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '10px', fontSize: '0.84rem', color: '#f59e0b' }}>
          ⚠️ Only Super Admins can create or edit roles.
        </div>
      )}

      {modal !== null && (
        <RoleModal role={modal === 'add' ? null : modal} onClose={() => setModal(null)} onSave={handleSave} />
      )}
    </div>
  );
};

const overlayS    = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' };
const modalS      = { background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '16px', width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-lg)' };
const modalHeaderS= { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid var(--border-color)' };
const closeBtnS   = { background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1.2rem' };
const inputS      = { padding: '9px 14px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '0.88rem', outline: 'none', width: '100%' };
const labelS      = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' };
const primaryBtnS = { padding: '9px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white' };
const cancelBtnS  = { padding: '9px 18px', borderRadius: '8px', border: '1px solid var(--border-color)', cursor: 'pointer', fontSize: '0.88rem', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' };
const actionBtnS  = { padding: '5px 10px', borderRadius: '6px', border: '1px solid var(--border-color)', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, background: 'var(--bg-tertiary)' };

export default RoleManagement;
